<template>
<div class="chapter">
<div class="inner_page">
<h1 class="chapter"><span class="ch">CHAPTER 1</span><br/>For You and Your Friends</h1>
<p class="nonindent">What is more fun than getting together with friends and working on crafts together? Whether you’re having a sleepover, facing a boring Saturday afternoon, or celebrating a birthday, these projects are perfect for doing with your BFFs.</p>
<p class="indent">Let’s face it, those storebought jewelry-making kits are so expensive—plus, you’re limited to the design options they give you. With washi tape, you don’t need to spend tons of money to make your own cute accessories, and you can choose your own colors and patterns!</p>
<p class="indent">In this chapter, you will find tons of great ideas for decorating your electronics, as well as instructions for how to create your own unique jewelry and accessories. You can quickly customize all of these ideas to create something that matches your personal style. Retro? Sporty? Sophisticated? Bold? You name it, you can make it! So, what are you waiting for? Let’s get started!</p>
<h2 class="section" id="sec1">Laptop Sleeve</h2>
<div class="image"><img alt="" src="images/IMG_2510_fmt.jpg"/></div>
<p class="rm">If you are the kind of person who likes to change things up all the time, then this project is perfect for you! You can use this same technique to cover practically any electronic device, like a tablet or iPad. With only a few materials, you can create an awesome cover that shows off your favorite colors and patterns.</p>
<h3 class="m">MATERIALS:</h3>
<p class="ri">Posterboard</p>
<p class="ri">Pencil</p>
<p class="ri">Ruler</p>
<p class="ri">Scissors</p>
<p class="ri">Washi tape</p>
<p class="ri">Magnetic dots (optional)</p>
<ol>
<li>Wrap the posterboard over your electronic device to get a measurement for the cover. The posterboard should fit around your electronic device twice. It should wrap around the device like a book cover with a few inches of extra space on the ends.<br/>
<div class="image"><img alt="" src="images/IMG_2496_fmt.jpg"/></div>
</li>
<li>Using your pencil and ruler, create an outline of where you should cut the posterboard to fit your device. Leave about one inch on each side just in case you make any mistakes!<br/>
<div class="image"><img alt="" src="images/IMG_2497_fmt.jpg"/></div>
</li>
<li>Once you have measured the posterboard, cut out the part you are going to use.<br/>
<div class="image"><img alt="" src="images/IMG_2501_fmt.jpg"/></div>
</li>
<li>Cover the posterboard with washi tape. Make sure you wrap around the front and back of the posterboard you just cut. You can alternate between different colors and patterns of washi tape for a unique design.<br/>
<div class="image"><img alt="" src="images/IMG_2503_fmt.jpg"/></div>
</li>
<li>Add some self-adhesive magnetic dots on the inside of the cover to create a closure for your cover (optional). The magnetic dots will keep the device from sliding out.<br/>
<div class="image"><img alt="" src="images/IMG_2506_fmt.jpg"/></div>
</li>
<li>All done! Now you have a totally unique design for your electronic device.</li>
</ol>
<h2 class="section" id="sec2">Phone Case Cover</h2>
<div class="image"><img alt="" src="images/C12_FinishedAlternate5_fmt.jpg"/></div>
<p class="rm">Turn your phone case from drab to fab in just a few easy steps! It is so easy and fun to make your own custom case using just a few pieces of washi tape and a blank case. The possibilities are endless! You can make a bunch of different ones to match your outfit, or even the season.</p>
<h3 class="m">MATERIALS:</h3>
<p class="ri">Blank phone case</p>
<p class="ri">Damp cloth</p>
<p class="ri">Washi tape</p>
<p class="ri">Scissors</p>
<p class="ri">Glitter or stickers</p>
<p class="ri">Superglue (optional)</p>
<p class="ri">Rubber gloves (optional)</p>
<p class="ri">Mod Podge</p>
<p class="ri">Brush</p>
<ol>
<li>Completely wipe the surface of your phone case with a damp cloth and let dry.<br/>
<div class="image"><img alt="" src="images/C12_Step1Alternate_fmt.jpg"/></div>
</li>
<li>In my example, I used three different tape designs. To follow my pattern, grab your first tape design and start laying strips of tape in whatever direction you’d like. Then add the other types of tape—mix it up by using different colors and patterns.<br/>
<div class="image"><img alt="" src="images/C12_Step2Alternate3_fmt.jpg"/></div>
</li>
<li>Fold the tape over the edges of the case, and trim the excess washi tape using a pair of scissors.<br/>
<div class="image"><img alt="" src="images/C12_Step3Alternate1_fmt.jpg"/></div>
</li>
<li>Now you can add any extra embellishments like glitter or stickers. Use a superglue to add any extra design elements to the case. Superglue can get messy; make sure to wear rubber gloves if you use it, protect your workspace with newspaper, and be careful not to spill or drip the glue.<br/>
<div class="image"><img alt="" src="images/C12_Finished_fmt.jpg"/></div>
</li>
<li>To seal and protect the design, brush Mod Podge over your case and embellishments. Allow the case to dry for a few hours between coats. The number of coats may vary between cases. Normally, 2 or 3 coats are perfect for a shiny and smooth surface.</li>
<li>Once the final coat of Mod Podge is dry, your case is ready to use!</li>
</ol>
<div class="side">
<h4 class="sbh">TRY THIS!</h4>
<p class="sb">There are many ways to spice up this basic design, so don’t feel limited to using only washi tape. Try adding studs, rhinestones, glitter, feathers, or stickers to make the case your own.</p>
</div>
<h2 class="section" id="sec3">Bookmark</h2>
<div class="image"><img alt="" src="images/IMG_7871_fmt.jpg"/></div>
<p class="rm">Love reading? Whether it’s a book for English class or just for fun, you need to know where you left off. Try creating bookmarks for all of the books you are currently reading, and it will definitely make reading and studying more fun. You can even customize them with quotes from your favorite books for an extra personal touch. This project is so easy that you can whip up a whole bunch of them in no time!</p>
<h3 class="m">MATERIALS:</h3>
<p class="ri">Scissors</p>
<p class="ri">Ruler</p>
<p class="ri">Posterboard</p>
<p class="ri">Washi tape</p>
<p class="ri">Hole punch (optional)</p>
<p class="ri">Ribbon (optional)</p>
<ol>
<li>Measure and cut out a 6" × 2" piece of posterboard. This is the base of your bookmark.<br/>
<div class="image"><img alt="" src="images/C14_Step1_fmt.jpg"/></div>
</li>
<li>Start layering pieces of washi tape on the bookmark. Try creating different effects by layering the pieces diagonally, vertically, or horizontally.<br/>
<div class="image"><img alt="" src="images/C14_Step2_fmt.jpg"/></div>
</li>
<li>This step is optional. If you’d like a little extra customization, you can punch a hole at the top of the bookmark, and loop a ribbon through the hole.</li>
<li>Your bookmark is now ready to use!</li>
</ol>
<h2 class="section" id="sec4">Washi Bead Necklace</h2>
<div class="image"><img alt="" src="images/IMG_2457_fmt.jpg"/></div>
<p class="rm">Jewelry can add style to any outfit. But buying tons of necklaces can cost a lot of money, and sometimes it’s hard to find the exact color you’re looking for. Luckily, creating a stylish necklace is easy with washi tape. You can make your own beads, and try out tons of cool color combinations. Wear a different one with each of your outfits, or wear your favorite one every day. You can also try experimenting with different paper shapes to create different patterns and styles.</p>
<h3 class="m">MATERIALS:</h3>
<p class="ri">Ruler</p>
<p class="ri">Card stock</p>
<p class="ri">Scissors</p>
<p class="ri">Washi tape</p>
<p class="ri">Necklace chain</p>
<p class="ri">Pencil</p>
<ol>
<li>Cut a small strip of card stock. It should measure roughly <sup class="num">1</sup>⁄<sub class="den">2</sub>" × 2". Most washi tapes are <sup class="num">1</sup>⁄<sub class="den">2</sub>" wide, but you can adjust the size of the paper to fit the width of your washi tape if you need to.<br/>
<div class="image"><img alt="" src="images/IMG_2446_fmt.jpg"/></div>
</li>
<li>Wrap the narrow edge of the strip of paper around a pencil. This step makes it easier to place the washi tape, and gives the paper its “bead” shape.<br/>
<div class="image"><img alt="" src="images/IMG_2449_fmt.jpg"/></div>
</li>
<li>Once you have wrapped the card stock around the pencil, you can wrap your washi tape around it to create the bead. You only need to wrap it around once, but if you don’t want to be able to see through the tape, wrap it around a few more times.<br/>
<div class="image"><img alt="" src="images/IMG_2452_fmt.jpg"/></div>
</li>
<li>Cut the excess tape and remove your bead from the pencil. Repeat steps 1 through 4 to create plenty of beads to fill your necklace. Use any combination of tape you choose.<br/>
<div class="image"><img alt="" src="images/IMG_2454_fmt.jpg"/></div>
</li>
<li>String the beads you created onto your necklace in a pattern you like.</li>
</ol>
<h2 class="section" id="sec5">Initial Necklace</h2>
<div class="image"><img alt="" src="images/C16_Finished_fmt.jpg"/></div>
<p class="rm">Personalized accessories are always in style. But skip the overpriced mall versions and make this monogrammed pendant necklace at home. Actually, create a bunch and give them to your friends and family. You can change the font of the letter using different stickers, and experiment with cool backgrounds. If necklaces aren’t your style, try adding the pendant to a keychain instead!</p>
<h3 class="m">MATERIALS:</h3>
<p class="ri">Card stock</p>
<p class="ri">Scissors</p>
<p class="ri">Washi tape</p>
<p class="ri">Large hole punch, with a cool shape (or scissors)</p>
<p class="ri">Small regular hole punch (the circle kind)</p>
<p class="ri">Alphabet stickers</p>
<p class="ri">Mod Podge Dimensional Magic</p>
<p class="ri">Paper clip or toothpick</p>
<p class="ri">Jump ring</p>
<p class="ri">Necklace chain</p>
<ol>
<li>Start by cutting out a piece of card stock big enough to be able to punch out the design with your shape hole punch at least once.<br/>
<div class="image"><img alt="" src="images/C16_Step1_fmt.jpg"/></div>
</li>
<li>Layer some pieces of washi tape on the piece of card stock you just cut. Since the pendant will be relatively small, sticking with only one kind of tape may look the best.<br/>
<div class="image"><img alt="" src="images/C16_Step2_fmt.jpg"/></div>
</li>
<li>Now, punch out one (or more) shapes with the shape hole punch. If you don’t have a hole punch that fits the bill, just cut out shapes yourself with a sharp pair of scissors.<br/>
<div class="image"><img alt="" src="images/C16_Step3_fmt.jpg"/></div>
</li>
<li>Add a monogram letter (or any sticker you like!) to the shape for decoration. You can also draw your own letters with 3D paint or markers, if you don’t have letter stickers.<br/>
<div class="image"><img alt="" src="images/C16_Step4_fmt.jpg"/></div>
</li>
<li>Cover the pendant with Mod Podge Dimensional Magic. This seals the design so your sticker won’t come off, and the shiny finish really makes the pendant pop.<br/>
<div class="image"><img alt="" src="images/C16_Step5_fmt.jpg"/></div>
</li>
<li>Use a paper clip or toothpick to spread the Mod Podge so that it completely covers the sticker, and then allow it to dry for three hours. If you need to add another layer, apply it before moving on to the next step, and allow it to dry.<br/>
<div class="image"><img alt="" src="images/C16_Step6_fmt.jpg"/></div>
</li>
<li>Use the smaller regular hole punch to create a hole at the top of the pendant for the jump ring (that’s the metal circle that’ll connect the pendant to the necklace chain).<br/>
<div class="image"><img alt="" src="images/C16_Step7_fmt.jpg"/></div>
</li>
<li>Loop your jump ring through the pendant.<br/>
<div class="image"><img alt="" src="images/C16_Step8_fmt.jpg"/></div>
</li>
<li>Finally, thread your newly created pendant on a necklace chain.</li>
</ol>
<div class="side">
<h4 class="sbh">MOD PODGE?</h4>
<p class="sb">Mod Podge is a great sealer for all types of arts and crafts projects. You can find it in the craft glue section of most craft stores. Look for Mod Podge Dimensional Magic to add a cool 3D effect to your project!</p>
</div>
<h2 class="section" id="sec6">Braided Friendship Bracelet</h2>
<div class="image"><img alt="" src="images/C17_Finished_fmt.jpg"/></div>
<p class="rm">Most friendship bracelets are made from thread or those little rubber bands. Try a different spin on them using washi tape! These bracelets take less than ten minutes to make. They’re a great and simple project to make with your best friends while you’re watching a movie. Try making them in your favorite colors, or check the sidebar for some cool color meanings. Just make sure you don’t get these bracelets wet!</p>
<h3 class="m">MATERIALS:</h3>
<p class="ri">Scissors</p>
<p class="ri">Washi tape</p>
<ol>
<li>Pull out a piece of washi tape long enough to wrap around your wrist twice, then fold it onto itself to create a single strand and all of the adhesive is covered. <br/>
<div class="image"><img alt="" src="images/C17_Step1_fmt.jpg"/></div>
</li>
<li>Cut the strand you just folded in half. You should now have two strands of washi tape. Repeat these two steps until you have four strands of washi tape. You’ll only need three strands for this project, so put aside the extra strand to use for another bracelet.<br/>
<div class="image"><img alt="" src="images/C17_Step2_fmt.jpg"/></div>
</li>
<li>Line up all three of your strands, and wrap one end with washi tape to keep them all in place while you are braiding. To make it easier, you can tape the end of the strands onto a desk or other surface. Start braiding the strands by looping first the right strand over the center strand, then the left strand over this new center strand. Repeat these two steps, braiding one strand over another in the same pattern.<br/>
<div class="image"><img alt="" src="images/C17_Step3_fmt.jpg"/></div>
</li>
<li>Finish the bracelet by closing the braid with a piece of washi tape. Trim off any extra strands sticking out to make the ends even. Put on your bracelet, and connect the ends together with a piece of washi tape. <br/>
<div class="image"><img alt="" src="images/C17_Step4_fmt.jpg"/></div>
</li>
</ol>
<div class="side">
<h4 class="sbh">COLOR MEANINGS!</h4>
<ul class="i">
<li>Yellow – Happiness</li>
<li>Blue – Loyalty</li>
<li>Green – Growth</li>
<li>Purple – Imagination</li>
<li>Pink – Love</li>
<li>Red – Confidence</li>
</ul>
</div>
<h2 class="section" id="sec7">Toilet Paper Roll Bracelet</h2>
<div class="image"><img alt="" src="images/IMG_8365_fmt.jpg"/></div>
<p class="rm">Who would have thought that a toilet paper roll could actually be fashionable? Since you completely cover the ugly brown roll with washi tape, no one will ever know you used one! Whenever you finish a toilet paper roll, or even a paper towel roll, save it. You can create tons of bangles from only one roll.</p>
<h3 class="m">MATERIALS:</h3>
<p class="ri">Toilet paper roll</p>
<p class="ri">Scissors</p>
<p class="ri">Washi tape</p>
<p class="ri">Hole punch</p>
<p class="ri">Craft thread</p>
<ol>
<li>Start by making sure your toilet paper roll is clean and free of any toilet paper or adhesive. Don’t worry too much about removing every last scrap, as it will all be covered by the tape.<br/>
<div class="image"><img alt="" src="images/C18_Step1_fmt.jpg"/></div>
</li>
<li>Grab your roll, and cut it open from end to end, the long way. Make sure your cut is straight.<br/>
<div class="image"><img alt="" src="images/C18_Step2_fmt.jpg"/></div>
</li>
<li>Once you have cut it vertically, cut a horizontal piece out of the toilet paper roll. This will be the bracelet.<br/>
<div class="image"><img alt="" src="images/C18_Step3_fmt.jpg"/></div>
</li>
<li>Start layering pieces of washi tape over the band you just created. You can layer tape vertically, horizontally, or diagonally—however you like!<br/>
<div class="image"><img alt="" src="images/IMG_8350_fmt.jpg"/></div>
</li>
<li>Using a small hole punch, make a hole at each short end of the band.<br/>
<div class="image"><img alt="" src="images/IMG_8356_fmt.jpg"/></div>
</li>
<li>Loop some craft thread through each hole to close the band, and secure with a loose knot. Make sure the bracelet fits over your hand, adjusting the length of the thread if necessary; tighten the knot, and cut off any extra thread. Now your bracelet is ready to wear.<br/>
<div class="image"><img alt="" src="images/IMG_8357_fmt.jpg"/></div>
</li>
</ol>
<h2 class="section" id="sec8">Popsicle Stick Bracelet</h2>
<div class="image"><img alt="" src="images/IMG_2444_fmt.jpg"/></div>
<p class="rm">If you’re looking for a different kind of bracelet, here’s another idea! This type of bracelet is strong enough to hold some extra sparkle—like some rhinestones, glitter, or other embellishments. You can buy craft sticks at a craft supply shop or the dollar store and make them into bracelets in no time.</p>
<h3 class="m">MATERIALS:</h3>
<p class="ri">Popsicle or flat craft sticks</p>
<p class="ri">Water, pot, tongs</p>
<p class="ri">Glasses or mugs</p>
<p class="ri">Scissors</p>
<p class="ri">Washi tape</p>
<p class="ri">Embellishments (such as markers, glitter, rhinestones, stickers, feathers)</p>
<p class="ri">Glue (if using embellishments)</p>
<p class="ri">Mod Podge and brush (optional)</p>
<ol>
<li>You’ll need to soak the wooden Popsicle sticks in water in order for the wood to become pliable enough to bend into a bracelet shape. Put them in a bowl of water and let them soak overnight.</li>
<li>The next day, have a parent help you boil water in a pot. Let the sticks soak in the boiling water while the pot is still on the stove for 20–30 minutes to help make them even more pliable.</li>
<li>While the sticks are soaking in the boiling water, find a glass or mug that’s roughly the same size as your wrist. You’re going to use this to form the shape of the bracelet. The wood will stretch a bit to get over your hand, but you don’t want to make the bangle too small, as it could crack when you try to slide it on.</li>
<li>Have a parent use tongs to remove the Popsicle sticks from the pot one at a time, and place them inside the rim of your chosen glass or cup to be shaped before they cool off. If you wait for the sticks to cool, they will crack when you put them in the cups.<br/>
<div class="image"><img alt="" src="images/IMG_2437_fmt.jpg"/></div>
</li>
<li>Once the Popsicle sticks are completely dry and you do not see any water on them, they should easily slide out of the cups. This can take anywhere from 24–48 hours.</li>
<li>Decorate the bent stick bracelets with washi tape by wrapping the tape down the stick. For an extra touch, add your name or a word with markers or pens, or add glitter, rhinestones, or any other decorations. You can seal the design with Mod Podge, if you like.</li>
<li>Once it’s decorated to your liking, your bracelet is ready to wear!<br/>
<div class="image"><img alt="" src="images/IMG_2439_fmt.jpg"/></div>
</li>
</ol>
<h2 class="section" id="sec9">Feather Dangle Earrings</h2>
<div class="image"><img alt="" src="images/IMG_7836_fmt.jpg"/></div>
<p class="rm">The look of feather earrings is fun and cute, but the retail versions can be really pricey. Making them out of inexpensive, easy-to-find materials can get you the look you need without burning through cash. You can make them to match your school colors, favorite outfit, or make each earring different, for a really quirky look!</p>
<h3 class="m">MATERIALS:</h3>
<p class="ri">Washi tape</p>
<p class="ri">Craft scissors</p>
<p class="ri">Hole punch</p>
<p class="ri">Earring hooks (2)</p>
<p class="ri">Pliers</p>
<ol>
<li>Pull out approximately 2" of washi tape. (The measurement doesn’t need to be exact.)</li>
<li>Fold the washi tape back onto itself so that all the adhesive is covered, making sure to press out any creases or bubbles. Cut off the folded piece.<br/>
<div class="image"><img alt="" src="images/C110_Step2_fmt.jpg"/></div>
</li>
<li>Repeat these steps with another piece of washi tape. Once you have two folded strips, you can move on.<br/>
<div class="image"><img alt="" src="images/C110_Step3_fmt.jpg"/></div>
</li>
<li>If you want the feathers to be the same size, stack the two pieces of folded tape on top of each other and fold them lengthwise (the long way).<br/>
<div class="image"><img alt="" src="images/C110_Step4_fmt.jpg"/></div>
</li>
<li>Cut a half-oval shape on the folded washi tape with your scissors, making sure you cut through both pieces.<br/>
<div class="image"><img alt="" src="images/C110_Step5_fmt.jpg"/></div>
</li>
<li>When you unfold the pieces of tape, you should have two oval shapes.</li>
<li>Fold the pieces of tape back in and make a bunch of short cuts on the sides. If you don’t want the cuts to be even, you can freehand the cuts, or you can use a pencil to mark the areas you would like to cut.<br/>
<div class="image"><img alt="" src="images/C110_Step7_fmt.jpg"/></div>
</li>
<li>Unfold your washi tape, and you should now have two cute little feathers! Separate each cut with your fingers to make the pieces stick out more.<br/>
<div class="image"><img alt="" src="images/C110_Step8_fmt.jpg"/></div>
</li>
<li>Next, grab the small hole punch and make a hole at the top of the “feather.” If you don’t have a hole punch, you can carefully puncture a small hole with a pen or the tip of your scissors.<br/>
<div class="image"><img alt="" src="images/C110_Step9_fmt.jpg"/></div>
</li>
<li>Finally, loop an earring hook through the hole you just made. The feather should slip onto the earring hook easily, but for extra security you can use pliers to adjust the earring hook open, and then to close it again. Now your earrings are ready to wear!<br/>
<div class="image"><img alt="" src="images/C110_Step10_fmt.jpg"/></div>
</li>
</ol>
<div class="side">
<h4 class="sbh">TRY THIS!</h4>
<p class="sb">To up the cool factor of these earrings, try adding beads or using real feathers from a craft store.</p>
</div>
<h2 class="section" id="sec10">Stud Earrings</h2>
<div class="image"><img alt="" src="images/IMG_7967_fmt.jpg"/></div>
<p class="rm">When it comes to accessories, sometimes simplicity is best. Stud earrings are perfect for daily use. Unlike dangle earrings, studs won’t get caught in anything, and they aren’t too heavy on your ears. A huge plus is that you can make these earrings quickly in a bunch of different colors to match any of your outfits. These studs are also a great handmade gift for you to give friends and family.</p>
<h3 class="m">MATERIALS:</h3>
<p class="ri">White card stock</p>
<p class="ri">Washi tape</p>
<p class="ri">Hole punch (regular circle punch or fun shapes)</p>
<p class="ri">Scissors</p>
<p class="ri">Mod Podge Dimensional Magic (or the Mod Podge of your choice)</p>
<p class="ri">Brush</p>
<p class="ri">Superglue</p>
<p class="ri">Earring posts (2)</p>
<p class="ri">Gloves</p>
<ol>
<li>First, cut a strip of card stock. Size isn’t too important—just make sure you have enough space to punch or cut out your designs.<br/>
<div class="image"><img alt="" src="images/IMG_7906_fmt.jpg"/></div>
</li>
<li>Place strips of washi tape over the card stock. The white card stock makes the washi tape designs brighter and easier to see.<br/>
<div class="image"><img alt="" src="images/IMG_7917_fmt.jpg"/></div>
</li>
<li>Punch holes in the washi tape–covered card stock strip with your hole punch. If you don’t have a hole punch, try creating designs freehand with scissors.<br/>
<div class="image"><img alt="" src="images/IMG_7923_fmt.jpg"/></div>
</li>
<li>Since you might be wearing the earrings daily, you want to make sure the washi tape will not rip or get damaged. To protect the pattern, seal the design with Dimensional Magic or any Mod Podge finish you prefer. Allow the designs to dry for a few hours.<br/>
<div class="image"><img alt="" src="images/IMG_7937_fmt.jpg"/></div>
</li>
<li>Once the shapes are dry, grab some superglue and attach them to your earring posts. Superglue can be really messy—and sticky!—so make sure you protect your work area with newspaper, wear gloves, and be careful not to spill any glue.<br/>
<div class="image"><img alt="" src="images/IMG_7956_fmt.jpg"/></div>
</li>
<li>Allow the superglue to dry for at least 30 minutes, and your designs are ready to wear! These stud earrings should be okay for swimming and showers as long as you use a sealer like Modge Podge—but to be extra cautious, remove them before going into the water.</li>
</ol>
<h2 class="section" id="sec11">Bobby Pin Flower</h2>
<div class="image"><img alt="" src="images/IMG_2339_fmt.jpg"/></div>
<p class="rm">Natural flowers are beautiful to wear in your hair, but they can be hard to find, difficult to position exactly right, and, unfortunately, they will dry up within a few days—if they even last that long! With these flower hairpins, you will never have to worry about that. You can layer the petals to create a bolder look, or just create a simple everyday pin to wear to school. The flower pins are perfect hair accents for a special event, like a school dance or holiday party. Let’s get started!</p>
<h3 class="m">MATERIALS:</h3>
<p class="ri">Wide washi tape</p>
<p class="ri">Scissors</p>
<p class="ri">Small hole punch (optional)</p>
<p class="ri">Bobby pin (or bobby pin base)</p>
<p class="ri">Superglue</p>
<p class="ri">Gloves</p>
<ol>
<li>Start with a 2"-wide washi tape. Pull out a strip of the washi tape measuring about 2" long, and then fold the tape onto itself so that it measures only 1" and all the adhesive is covered. <br/>
<div class="image"><img alt="" src="images/IMG_2311_fmt.jpg"/></div>
</li>
<li>Fold the strip of tape in half the long way to make it easier to cut, and then cut a half-oval shape for the petals. When you unfold the tape, it should look like a half oval. Repeat steps 1 and 2 about five to seven times, depending on how full you want the flower to look. <br/>
<div class="image"><img alt="" src="images/IMG_2313_fmt.jpg"/></div>
</li>
<li>Make sure the petal is still folded. Using a pair of scissors or a tiny hole punch, cut a tiny circle through the uncut end of the petals. Don’t cut it too close to the edge, or the petals may rip when attaching them to the bobby pin.<br/>
<div class="image"><img alt="" src="images/IMG_2315_fmt.jpg"/></div>
</li>
<li>Pull the petals through the bobby pin, one by one. <br/>
<div class="image"><img alt="" src="images/IMG_2317_fmt.jpg"/></div>
</li>
<li>Use superglue to secure the petals exactly where you want them, if necessary. Superglue can be really messy—and sticky!—so make sure you protect your work area with newspaper, wear gloves, and be careful not to spill any glue. (If your bobby pin has a base for attachments, you can just glue the flower petals on it.) <br/>
<div class="image"><img alt="" src="images/IMG_2318_fmt.jpg"/></div>
</li>
<li>For the center of the petals, just cut out a small strip of washi tape and crumple it up into a little ball. <br/>
<div class="image"><img alt="" src="images/IMG_2320_fmt.jpg"/></div>
</li>
<li>Place the little ball in the middle of all of the flower petals and secure it with superglue.<br/>
<div class="image"><img alt="" src="images/IMG_2322_fmt.jpg"/></div>
</li>
<li>Allow the glue to dry for a few minutes, and your flower is ready to wear!<br/>
<div class="image"><img alt="" src="images/IMG_2331_fmt.jpg"/></div>
</li>
</ol>
<div class="side">
<h4 class="sbh">BOBBY PIN BASES</h4>
<p class="sb">You can find bobby pin bases at most craft stores. These bobby pins have a flat disc attached to them, making it easy to add your designs quickly.</p>
</div>
<h2 class="section" id="sec12">Bow Ring</h2>
<div class="image"><img alt="" src="images/C113_Finished_fmt.jpg"/></div>
<p class="rm">Who says bows are just for your hair? These super cute rings make any outfit more fun. Once you’ve got the hang of making the bows, you can add them to bobby pins or use them to decorate other projects in this book, like the Tin Can Organizers (see <span href="#/c02" title="Link target outside of Sample">Chapter 2: For Your Room</span>).</p>
<h3 class="m">MATERIALS:</h3>
<p class="ri">Washi tape (width your preference)</p>
<p class="ri"><sup class="num">1</sup>⁄<sub class="den">2</sub>"-wide washi tape</p>
<p class="ri">Scissors</p>
<p class="ri">Ring base</p>
<p class="ri">Superglue (optional)</p>
<p class="ri">Rubber gloves (optional)</p>
<ol>
<li>Pull out 8" of washi tape. Once you see how this bow comes out, you can use more or less than 8", depending on what size you want your bow to be.</li>
<li>Fold the washi tape onto itself so that you have a 4" strip. Try to get all of the edges to line up, and avoid getting any air bubbles, pockets, or creases.<br/>
<div class="image"><img alt="" src="images/C113_Step2_fmt.jpg"/></div>
</li>
<li>Once you have folded the tape, cut it off the roll with your scissors, and make sure both of the ends are even.<br/>
<div class="image"><img alt="" src="images/C113_Step3_fmt.jpg"/></div>
</li>
<li>Fold the ends of the strip towards the center to make two loops. <br/>
<div class="image"><img alt="" src="images/C113_Step4_fmt.jpg"/></div>
</li>
<li>Use a strip of the <sup class="num">1</sup>⁄<sub class="den">2</sub>"-wide washi tape to wrap around the center and secure the loops of the bow in place. This may be tricky at first, but if the loops come undone, just try again. <br/>
<div class="image"><img alt="" src="images/C113_Step5_fmt.jpg"/></div>
</li>
<li>Grab a long strip of your <sup class="num">1</sup>⁄<sub class="den">2</sub>"-wide washi tape, and this time wrap it through the ring base and all the way around the front of the bow to attach it to the ring. You can also use some superglue for added security.<br/>
<div class="image"><img alt="" src="images/C113_Step6_fmt.jpg"/></div>
</li>
<li>Once you have wrapped it around a few times, cut the tape and be sure no edges are showing in the front. You are all done!<br/>
<div class="image"><img alt="" src="images/C113_Step7_fmt.jpg"/></div>
</li>
</ol>
<h2 class="section" id="sec13">Barrette</h2>
<div class="image"><img alt="" src="images/IMG_8092_fmt.jpg"/></div>
<p class="rm">Not a fan of really girly bows? Do you prefer a more simple look for your hair? You can make cute patterned barrettes instead. Like the bows, you can create virtually any color barrette to match your outfit, or give them to friends as gifts. There are tons of ways to spice up the barrettes, too. You can find the white craft foam in bags or sheets at a craft store.</p>
<h3 class="m">MATERIALS:</h3>
<p class="ri">White craft foam</p>
<p class="ri">Scissors</p>
<p class="ri">Washi tape (at least 1" wide)</p>
<p class="ri">Mod Podge</p>
<p class="ri">Brush</p>
<p class="ri">Barrette clip</p>
<p class="ri">Superglue</p>
<p class="ri">Rubber gloves</p>
<ol>
<li>Cut out a small piece of white craft foam, approximately <sup class="num">1</sup>⁄<sub class="den">2</sub>" × 1". You can increase or decrease these measurements depending on the size of barrette you would like.<br/>
<div class="image"><img alt="" src="images/IMG_8031_fmt.jpg"/></div>
</li>
<li>Place your desired washi tape over the strip you just cut out. The foam not only acts as a flexible base, but it also gives the washi tape colors a brighter look.<br/>
<div class="image"><img alt="" src="images/IMG_8038_fmt.jpg"/></div>
</li>
<li>Fold any excess washi tape over the edges to completely cover the white foam board. You should only see the washi tape pattern. <br/>
<div class="image"><img alt="" src="images/IMG_8046_fmt.jpg"/></div>
</li>
<li>Since these barrettes might be in your hair daily, it is really important to use a sealer on the design to protect it from wear and tear. Brush on a layer of Mod Podge and let it dry for a few hours. <br/>
<div class="image"><img alt="" src="images/IMG_8059_fmt.jpg"/></div>
</li>
<li>When the Mod Podge is dry, grab some superglue and glue a barrette clip onto the back of the foam board. Superglue can be really messy—and sticky!—so make sure you protect your work area with newspaper, wear gloves, and be careful not to spill any glue.<br/>
<div class="image"><img alt="" src="images/IMG_8065_fmt.jpg"/></div>
</li>
<li>Allow the glue to dry for a few minutes, and your new barrette is ready for use. <br/>
<div class="image"><img alt="" src="images/IMG_8075_fmt.jpg"/></div>
</li>
</ol>
<div class="side">
<h4 class="sbh">TRY THIS!</h4>
<p class="sb">Want to make your barrette a little more detailed? Use patterned scissors to cut plain tape into interesting layering combinations for a small but super cute variation.</p>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// c01.vue?589b4f6a